import { TractNRI, SubtractNRI } from 'components/nri-table';
import { AcreageBasisDropdown } from './acreage-basis-dropdown';
import { Uat } from '../types';
import { UatInterestInfo } from '../nri/uat-interest-info';
import styles from './uat-list.module.scss';
import classNames from 'classnames';
import { isNil } from 'lodash-es';
import { formatDecimalFull } from 'lib/ui';
import { useMemo } from 'react';

interface UatListProps {
  uats: Uat[];
  /** Owning subtract; the acreage basis is stored per (subtract, UAT). */
  subtractId?: string;
  selectedUatId: number | undefined;
  setSelectedUatId: (uatId: number) => void;
  slotNriByUwi?: Map<string, TractNRI>;
  selectedSlotId?: string;
}

/**
 * UAT list with optional Interest Info cards. Each UAT renders as a single
 * card row: details on the left, Interest Info on the right separated by a border.
 * @param {UatListProps} props - The component props.
 * @return {JSX.Element} The UAT list.
 */
export const UatList = ({
  uats,
  subtractId,
  selectedUatId,
  setSelectedUatId,
  slotNriByUwi,
  selectedSlotId,
}: UatListProps) => {
  const uatNriByName = useMemo(() => {
    if (!slotNriByUwi || !selectedSlotId) return new Map<string, SubtractNRI>();
    const slotData = slotNriByUwi.get(selectedSlotId);
    if (!slotData?.subtracts) return new Map<string, SubtractNRI>();

    const map = new Map<string, SubtractNRI>();
    for (const sub of slotData.subtracts) {
      if (sub.uat) {
        map.set(sub.uat, sub);
      }
    }
    return map;
  }, [slotNriByUwi, selectedSlotId]);

  return (
    <div className={styles.container}>
      <div className={styles.stickyHeader}>
        <h4 className={styles.title}>Unit Allocation Tracts</h4>
      </div>
      <div className={styles.scrollableContent}>
        {uats.map((uat) => {
          const nriData = uat.name ? uatNriByName.get(uat.name) : undefined;

          return (
            <div
              key={uat.id}
              onClick={() => setSelectedUatId(uat.id)}
              className={classNames(styles.uatCard, {
                [styles.selected]: uat.id === selectedUatId,
              })}
            >
              <div className={styles.uatLeft}>
                <div className={styles.uatHeader}>
                  <span className={styles.uatName}>{uat.name ?? '--'}</span>
                </div>
                <div className={styles.uatMeta}>
                  <div>
                    Allocation Value:{' '}
                    {uat.allocationValue
                      ? formatDecimalFull(String(uat.allocationValue))
                      : '--'}
                  </div>
                  <div>
                    Allocation Factor:{' '}
                    {isNil(uat.allocationFactor) ? '--' : uat.allocationFactor}
                  </div>
                  <div>
                    Subtract to UAT:{' '}
                    {isNil(uat.subtractToUatPct)
                      ? '--'
                      : `${uat.subtractToUatPct}%`}
                  </div>
                  <div>
                    UAT to Subtract:{' '}
                    {isNil(uat.uatToSubtractPct)
                      ? '--'
                      : `${uat.uatToSubtractPct}%`}
                  </div>
                </div>
              </div>
              {(nriData || subtractId) && (
                <div className={styles.uatRight}>
                  {nriData && (
                    <UatInterestInfo
                      uatNri={nriData.uatNri ?? null}
                      uatWi={nriData.uatWi ?? null}
                      nriCalculationId={nriData.nriCalculationId ?? null}
                      verifications={nriData.verifications}
                      subtractId={subtractId}
                      uatGisUuid={uat.gisUuid}
                      acreageBasis={uat.acreageBasis?.acreageBasis}
                      storedOverlapFraction={uat.acreageBasis?.overlapFraction}
                    />
                  )}
                  {/*
                    Lives in this column rather than beside the tract details:
                    the details column is ~120px wide, which truncated the
                    selection to "L..", while Interest Info has the room to
                    spare.
                  */}
                  {subtractId ? (
                    <AcreageBasisDropdown
                      subtractId={subtractId}
                      uatGisUuid={uat.gisUuid}
                      basis={uat.acreageBasis}
                      nriCalculationId={nriData?.nriCalculationId ?? null}
                    />
                  ) : (
                    /*
                      Future Development renders this same list without the
                      control, which the ticket scopes to Current Development.
                      The choice still applies per (subtract, UAT), so it moves
                      the NRI here too — shown read-only rather than leaving a
                      number that cannot be explained. Only legal is worth
                      saying: GIS is the default every UAT starts on.
                    */
                    uat.acreageBasis?.acreageBasis === 'legal' && (
                      <div className={styles.readOnlyBasis}>
                        Acreage basis: Legal acreage
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
